import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEARCH_SETTLE_MS, hubKey, useSearch, useSearchCount } from '../lib/useEventSearch';
import { useCombobox, useSettledCount } from '../lib/useCombobox';
import { getGeo, type Geo } from '../lib/personal';
import { BoxNote, SuggestPrice, boxRows, hasBoxNote, realActsFirst, sameDayIds, searchHref, useHubMeta } from './SearchParts';
import { fmtDateShort, fmtTime } from '../lib/format';
import { EventThumb, HubAvatar } from './Poster';
import { useLocale, useT } from '../i18n';
import { eventRowName } from '../lib/event-name';

/** Elements Tab may land on inside the dialog. */
const FOCUSABLE = 'a[href]:not([tabindex="-1"]), button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Site-wide search: opens over any page (masthead button or Ctrl/Cmd+K). */
const SearchOverlay = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [query, setQuery] = useState('');
  const [geo, setGeo] = useState<Geo | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const navigate = useNavigate();
  const t = useT();
  const locale = useLocale();
  const hubMeta = useHubMeta();

  // the same answer as the search page (one row per show), first eight shown; the real act's
  // page and dates before tribute acts' pages and nights
  const q = query.trim();
  const search = useSearch(open ? query : '');
  const { events: results, hubs, meta, pending, soon } = search;
  const items = boxRows(hubs.slice(0, 3), realActsFirst(results).slice(0, 8));
  const times = sameDayIds(results);
  const options = items.map((x) => x.href);
  // the settled-search counter: the search once its answer stood still, and a tapped result
  const tap = useSearchCount(search, SEARCH_SETTLE_MS);
  const pick = (i: number) => tap(items[i]?.hub);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };
  const box = useCombobox({
    count: q.length >= 2 ? options.length : 0,
    onPick: (i) => {
      pick(i);
      go(options[i]);
    },
    onEscape: onClose,
  });
  const said = useSettledCount(options.length, box.open && !pending);

  useEffect(() => {
    if (open) getGeo().then(setGeo);
  }, [open]);

  // iOS-safe scroll lock: position:fixed with scroll restore (overflow:hidden
  // alone doesn't lock iOS Safari), plus focus save/restore for a11y.
  useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement;
      setQuery('');
      const y = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${y}px`;
      document.body.style.width = '100%';
      setTimeout(() => inputRef.current?.focus(), 30);
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, y);
        (triggerRef.current as HTMLElement | null)?.focus?.();
      };
    }
  }, [open]);

  // Escape outside the input closes the dialog (in the input it first closes the list)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  /** Tab stays inside the dialog: past the last control it comes back to the first. */
  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const listShown = box.open && q.length >= 2 && options.length > 0;

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={trapTab}
      role="dialog"
      aria-modal="true"
      aria-label={t('Search')}
    >
      <div className="max-w-2xl mx-auto mt-[10vh] px-4 pb-16" ref={box.rootRef}>
        <form
          action="/search"
          method="get"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            const v = String(new FormData(e.currentTarget).get('q') ?? '').trim();
            if (v) go(searchHref(v));
          }}
          className="flex items-center gap-2 rounded-xl border-2 border-primary/60 bg-card pl-4 pr-1.5 py-1.5 shadow-2xl sm:pl-5"
        >
          <input
            ref={inputRef}
            type="search"
            name="q"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              box.setOpen(true);
            }}
            {...box.inputProps}
            aria-label={t('Search events')}
            placeholder={t('Artist, team, show, or city…')}
            className="min-w-0 flex-1 border-0 bg-transparent py-3 font-sans text-[17px] not-italic text-foreground outline-none focus-visible:outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
          />
          <button
            type="submit"
            className="h-[40px] shrink-0 rounded-md bg-primary px-5 text-[14.5px] font-semibold text-primary-foreground transition-colors hover:bg-amber-deep"
          >
            {t('Search')}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close search')}
            className="flex size-11 shrink-0 items-center justify-center text-[20px] text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </form>

        {q.length >= 2 && !pending && hasBoxNote(meta) && (
          <div className="border border-t-0 border-border bg-card px-4 py-3">
            <BoxNote meta={meta} query={q} onNavigate={onClose} onResult={() => tap()} />
          </div>
        )}
        <div {...box.listProps} aria-label={t('Search events')} className={listShown ? 'border border-t-0 border-border bg-card shadow-2xl' : 'hidden'}>
          {/* the pages the query names first: "sombr" → sombr's page with every date */}
          {listShown &&
            items.map((x, i) =>
              x.hub ? (
                <Link
                  key={hubKey(x.hub)}
                  to={x.href}
                  onClick={() => {
                    pick(i);
                    onClose();
                  }}
                  {...box.optionProps(i)}
                  className={`group flex min-h-[48px] items-center gap-4 border-b border-border px-4 py-2.5 last:border-0 ${box.active === i ? 'bg-muted' : 'hover:bg-muted'}`}
                >
                  <HubAvatar name={x.hub.name} src={x.hub.image} size={44} round={x.hub.kind === 'artist' || x.hub.kind === 'team'} />
                  <span className="min-w-0">
                    <span className="block truncate font-sans text-[15px] font-semibold text-foreground group-hover:text-primary">{x.hub.name}</span>
                    <span className="tabular block truncate font-sans text-[12px] text-muted-foreground">{hubMeta(x.hub, results, geo, (d) => fmtDateShort(d, locale))}</span>
                  </span>
                </Link>
              ) : (
                <Link
                  key={x.event.id}
                  to={x.href}
                  onClick={() => {
                    pick(i);
                    onClose();
                  }}
                  {...box.optionProps(i)}
                  className={`group flex min-h-[48px] items-center gap-4 border-b border-border px-4 py-2.5 last:border-0 ${box.active === i ? 'bg-muted' : 'hover:bg-muted'}`}
                >
                  <EventThumb event={x.event} size={44} date={false} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-sans text-[15px] font-medium text-foreground group-hover:text-primary">{eventRowName(x.event).title}{eventRowName(x.event).sub && <span className="font-normal text-muted-foreground"> · {eventRowName(x.event).sub}</span>}</span>
                    <span className="tabular block truncate font-sans text-[12px] text-muted-foreground">
                      {fmtDateShort(x.event.date, locale)}
                      {times.has(x.event.id) && x.event.time ? ` · ${fmtTime(x.event.time, locale)}` : ''} · {x.event.venue}, {x.event.city}
                    </span>
                  </span>
                  <SuggestPrice event={x.event} soon={soon.has(x.event.id)} />
                </Link>
              )
            )}
        </div>
        <p role="status" className="sr-only">
          {said !== null && q.length >= 2 ? t('{n} result|{n} results', { n: said }) : ''}
        </p>

        <p className="mt-4 text-center font-sans text-[12px] text-white/75 [@media(hover:none)]:hidden">{t('esc to close · enter to search')}</p>
      </div>
    </div>
  );
};

export default SearchOverlay;
